import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Row, Space, Switch } from 'antd'
import { PrimaryButton } from 'Components/button'
import { menuRoleMerchantType } from 'helpers/constanst'
import { isFormRequired, isFormError, formValidation } from 'helpers/format'
import { CreateRoleReq, RoleDetailReq, UpdateRoleReq } from 'Store/manage-role/types'
import { createRoleAPI, getRoleDetailAPI, updateRoleAPI } from 'Store/manage-role/api'
import { selectRoleDetail, selectRoleDetailEvent, selectRoleActionEvent } from 'Store/manage-role/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { LabelItem, AppInput, AppSelect } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/manage-role/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { selectDatas } from 'Store/config/selector'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
}

const RoleDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = ['name', 'merchant_type']
  const initForm = {
    name: '',
    description: '',
    merchant_type: '',
    status: 'active',
  } as CreateRoleReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateRoleReq = {
      ...formData,
    }
    dispatch(createRoleAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onGetDetail = (): void => {
    const req: RoleDetailReq = {
      id,
    }
    dispatch(getRoleDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateRoleReq = {
      ...formData,
      id,
    }
    dispatch(updateRoleAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }

    // get detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    }
  }, [visible])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const form = {
        ...detail,
      } as CreateRoleReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onUpdateData = (val: string, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const handleToggleStatus = (val: boolean) => {
    setFormData({
      ...formData,
      status: val ? 'active' : 'inactive',
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const title = isCreateMode ? 'เพิ่มข้อมูล Role' : 'แก้ไขข้อมูล Role'

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={FooterContent}
      width={'40%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={16}>
        <Col md={24} xs={24}>
          <AppInput
            name="name"
            label="ชื่อ Role"
            placeHolder="ระบุชื่อ Role"
            isRequired={isRequired('name')}
            isError={isError('name')}
            value={formData.name}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={24} xs={24}>
          <AppSelect
            name="merchant_type"
            label="Merchant Type"
            placeHolder="เลือกประเภท Merchant Type"
            isRequired={isRequired('merchant_type')}
            isError={isError('merchant_type')}
            value={formData?.merchant_type}
            onChange={onUpdateData}
            menu={menuRoleMerchantType}
            allowClear={false}
          />
        </Col>
        <Col md={24} xs={24}>
          <AppInput
            name="description"
            label="คำอธิบาย"
            placeHolder="ระบุคำอธิบาย"
            isRequired={isRequired('description')}
            isError={isError('description')}
            value={formData.description}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={24} xs={24}>
          <SwitchMargin>
            <Switch checked={formData.status === 'active'} onChange={handleToggleStatus} />
            &nbsp;
            <LabelItem label={`สถานะ : ${formData.status === 'active' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`} />
          </SwitchMargin>
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const SwitchMargin = styled.div`
  margin: 20px 0;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectRoleActionEvent(state),
    detail: selectRoleDetail(state),
    detailEvent: selectRoleDetailEvent(state),
    configs: selectDatas(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(RoleDetail)
